import React, { useState } from 'react';
import RobuxPanel from '../components/RobuxPanel';
import { EA_TAX_PCT } from '../lib/eaTax';
// @ts-ignore: allow side-effect CSS import without type declarations
import '../App.css';

const METHODS = [
  { value: 'cycle',         label: 'Cycle',          desc: 'Fast, pool mode — no supplier ID needed' },
  { value: 'targetedSnipe', label: 'Surgical Snipe', desc: 'Precise targeting — no supplier ID needed' },
  { value: 'snipeLimited',  label: 'Limited Snipe',  desc: 'Pool mode — no supplier ID needed' },
  { value: 'snipe',         label: 'Legacy Snipe',   desc: 'Requires supplier ID' },
];

const RISK_LEVELS = [
  { value: 1,  label: '1 — Maximum safety (lowest speed)' },
  { value: 2,  label: '2 — Safe (default)' },
  { value: 3,  label: '3 — Low ban risk (no refunds)' },
  { value: 4,  label: '4 — Medium ban risk (no refunds)' },
  { value: 5,  label: '5 — Increased ban risk (no refunds)' },
  { value: 6,  label: '6 — Ban mode (no refunds, max speed)' },
  { value: 7,  label: '7' },
  { value: 8,  label: '8' },
  { value: 9,  label: '9' },
  { value: 10, label: '10' },
  { value: 11, label: '11' },
];

const needsThreshold = (m: string) => m === 'targetedSnipe' || m === 'cycle';

const WORKERS = ['NOUR', 'KHALED', 'YOUSEF', 'JIMMY', 's7s', 'ABOOD', 'Rafat', 'Amira'];

const CreateOrderPage: React.FC = () => {
  // Which product the employee is working on. Remembered so a refresh doesn't
  // bounce someone back to the coins form mid-shift.
  const [tab, setTab] = useState<'coins' | 'robux'>(
    () => (sessionStorage.getItem('panelTab') === 'robux' ? 'robux' : 'coins'),
  );
  const switchTab = (t: 'coins' | 'robux') => { sessionStorage.setItem('panelTab', t); setTab(t); };

  const [adminKey, setAdminKey] = useState(sessionStorage.getItem('adminKey') || '');
  const [customerName, setCustomerName] = useState('');
  const [amount, setAmount] = useState('');
  const [platform, setPlatform] = useState('PS');
  const [transferMethod, setTransferMethod] = useState('cycle');
  // Where the coins come from: our own sellers (store customers selling to us)
  // or the FutTransfer market. Auto = ours when the pool covers the order.
  const [coinSource, setCoinSource] = useState<'auto' | 'own' | 'market'>('auto');
  const [buyNowThreshold, setBuyNowThreshold] = useState('');
  const [riskLevel, setRiskLevel] = useState('2');
  const [supplierID, setSupplierID] = useState('');
  const [orderPrice, setOrderPrice] = useState('');
  const [customer, setCustomer] = useState('');
  const [worker, setWorker] = useState(sessionStorage.getItem('worker') || '');
  const [autoFinishCycle, setAutoFinishCycle] = useState(true);
  const [cyclePrice, setCyclePrice] = useState('');
  const [topUpEnabled, setTopUpEnabled] = useState(false);
  const [topUpThreshold, setTopUpThreshold] = useState('150');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  // What the API says was bought vs what lands — for the welcome message.
  // Null when the create was a duplicate and the API returned no figures.
  const [figures, setFigures] = useState<{ gross: number; net: number; pct: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Cancel order state
  const [cancelName, setCancelName] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelResult, setCancelResult] = useState<'success' | 'error' | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Sync (sweep) state
  const [sweepLoading, setSweepLoading] = useState(false);
  const [sweepResult, setSweepResult] = useState<string | null>(null);
  const [sweepError, setSweepError] = useState<string | null>(null);

  // Audit (validate sheet) state
  type MissingOrder = { name: string; amount: number; orderPrice: string; customer: string; worker: string; status: string; created: string };
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [missing, setMissing] = useState<MissingOrder[] | null>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [addLoading, setAddLoading] = useState(false);
  const [addResult, setAddResult] = useState<string | null>(null);

  // Convert cycle → surgical state
  type SurgicalCandidate = {
    name: string; amount: number; delivered: number; pending: number;
    origin: 'cycle' | 'surgical'; savedRiskLevel: number | null;
    status: string | null; economyState: string | null; readyToConvert: boolean;
    isReady: boolean; cooldownRemaining: number; hours: number | null; minutes: number | null;
    cooldownMessage: string;
  };
  const [surgLoading, setSurgLoading] = useState(false);
  const [surgError, setSurgError] = useState<string | null>(null);
  const [surgResult, setSurgResult] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<SurgicalCandidate[] | null>(null);
  const [surgSelected, setSurgSelected] = useState<Record<string, boolean>>({});
  const [surgRisk, setSurgRisk] = useState<Record<string, string>>({});
  const [convertLoading, setConvertLoading] = useState(false);

  const handleSurgicalScan = async () => {
    if (!adminKey.trim()) { setSurgError('Enter your Admin Key first.'); return; }
    setSurgLoading(true);
    setSurgError(null);
    setSurgResult(null);
    setCandidates(null);
    sessionStorage.setItem('adminKey', adminKey.trim());
    try {
      const res = await fetch('/api/orders/__surgicalScan__', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminKey.trim()}` },
      });
      const data = await res.json();
      if (!res.ok) { setSurgError(data.error || 'Scan failed'); }
      else {
        const list: SurgicalCandidate[] = data.candidates || [];
        setCandidates(list);
        const sel: Record<string, boolean> = {};
        const risk: Record<string, string> = {};
        list.forEach((c) => {
          // Pre-select only the ones we can convert right now: already aborted,
          // past cooldown, and above the 6K surgical minimum.
          sel[c.name] = c.readyToConvert && c.isReady && c.pending >= 6;
          risk[c.name] = String(c.savedRiskLevel ?? 4);
        });
        setSurgSelected(sel);
        setSurgRisk(risk);
      }
    } catch {
      setSurgError('Network error — check your connection');
    } finally {
      setSurgLoading(false);
    }
  };

  const handleConvertSurgical = async () => {
    if (!candidates) return;
    const orders = candidates
      .filter((c) => surgSelected[c.name])
      .map((c) => ({ name: c.name, riskLevel: Number(surgRisk[c.name] || c.savedRiskLevel || 4) }));
    if (!orders.length) { setSurgError('Select at least one ready order to convert.'); return; }
    setConvertLoading(true);
    setSurgError(null);
    setSurgResult(null);
    try {
      const res = await fetch('/api/orders/__convertToSurgical__', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminKey.trim()}` },
        body: JSON.stringify({ orders }),
      });
      const data = await res.json();
      if (!res.ok) { setSurgError(data.error || 'Conversion failed'); }
      else {
        type Res = { name: string; ok: boolean; error?: string; detail?: string };
        const results: Res[] = data.results || [];
        const okNames = results.filter(r => r.ok).map(r => r.name);
        const banned = results.filter(r => !r.ok && r.error === 'tempBanned');
        const needsAbort = results.filter(r => !r.ok && r.error === 'needsAbort').map(r => r.name);
        const failed = results.filter(r => !r.ok && r.error !== 'tempBanned' && r.error !== 'needsAbort');
        setSurgResult(
          `Converted ${data.converted} order${data.converted === 1 ? '' : 's'} to surgical` +
          (okNames.length ? ` (${okNames.join(', ')})` : '') + '.' +
          (needsAbort.length ? ` ${needsAbort.length} still running — "Abort at next possibility" in the FutTransfer dashboard first, then Scan + Convert again: ${needsAbort.join(', ')}.` : '') +
          (banned.length ? ` ${banned.length} temp-banned — staying on cycle, retry after the ban clears: ${banned.map(b => `${b.name} (${b.detail})`).join(', ')}.` : '') +
          (failed.length ? ` ${failed.length} failed: ${failed.map(f => `${f.name} [${f.detail || f.error}]`).join(', ')}.` : '')
        );
        // Drop the converted ones from the list.
        setCandidates((prev) => prev ? prev.filter(c => !okNames.includes(c.name)) : prev);
      }
    } catch {
      setSurgError('Network error — check your connection');
    } finally {
      setConvertLoading(false);
    }
  };

  const handleAudit = async () => {
    if (!adminKey.trim()) { setAuditError('Enter your Admin Key first.'); return; }
    setAuditLoading(true);
    setAuditError(null);
    setMissing(null);
    setAddResult(null);
    sessionStorage.setItem('adminKey', adminKey.trim());
    try {
      const res = await fetch('/api/orders/__audit__', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminKey.trim()}` },
      });
      const data = await res.json();
      if (!res.ok) setAuditError(data.error || 'Validation failed');
      else {
        setMissing(data.missing);
        const sel: Record<string, boolean> = {};
        data.missing.forEach((m: MissingOrder) => { sel[m.name] = true; });
        setSelected(sel);
      }
    } catch {
      setAuditError('Network error — check your connection');
    } finally {
      setAuditLoading(false);
    }
  };

  const handleAddSelected = async () => {
    const names = Object.keys(selected).filter(n => selected[n]);
    if (!names.length) { setAuditError('Select at least one order to add.'); return; }
    setAddLoading(true);
    setAuditError(null);
    setAddResult(null);
    try {
      const res = await fetch('/api/orders/__addToSheet__', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminKey.trim()}` },
        body: JSON.stringify({ names }),
      });
      const data = await res.json();
      if (!res.ok) setAuditError(data.error || 'Failed to add orders');
      else {
        setAddResult(`Added ${data.added} order${data.added === 1 ? '' : 's'} to the sheet.`);
        setMissing((prev) => prev ? prev.filter(m => !data.addedNames.includes(m.name)) : prev);
      }
    } catch {
      setAuditError('Network error — check your connection');
    } finally {
      setAddLoading(false);
    }
  };

  const handleSweep = async () => {
    if (!adminKey.trim()) { setSweepError('Enter your Admin Key first.'); return; }
    setSweepLoading(true);
    setSweepError(null);
    setSweepResult(null);
    sessionStorage.setItem('adminKey', adminKey.trim());
    try {
      const res = await fetch('/api/orders/__sweep__', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminKey.trim()}`,
        },
      });
      const data = await res.json();
      if (!res.ok) { setSweepError(data.error || 'Sync failed'); }
      else {
        setSweepResult(
          `Checked ${data.checked} active order${data.checked === 1 ? '' : 's'} — ` +
          `${data.completed} newly completed${data.completed > 0 ? ` (${data.completedOrders.join(', ')})` : ''}.` +
          (data.errors ? ` ${data.errors} could not be checked.` : '')
        );
      }
    } catch {
      setSweepError('Network error — check your connection');
    } finally {
      setSweepLoading(false);
    }
  };

  const handleCreate = async () => {
    const name = customerName.trim();
    if (!name || !amount || !adminKey.trim()) return;
    if (coinSource !== 'own' && needsThreshold(transferMethod) && !buyNowThreshold.trim()) {
      setError('Price per 100K (buyNowThreshold) is required for this method.');
      return;
    }
    if (coinSource !== 'own' && transferMethod === 'snipe' && !supplierID.trim()) {
      setError('Supplier ID is required for Legacy Snipe method.');
      return;
    }
    setLoading(true);
    setError(null);
    sessionStorage.setItem('adminKey', adminKey.trim());
    if (worker) sessionStorage.setItem('worker', worker);
    try {
      const body: Record<string, unknown> = {
        customerName: name,
        amount: Number(amount),
        platform,
        transferMethod,
        riskLevel: Number(riskLevel),
        source: coinSource,
      };
      if (buyNowThreshold.trim()) body.buyNowThreshold = Number(buyNowThreshold);
      if (supplierID.trim()) body.supplierID = supplierID.trim();
      if (orderPrice.trim()) body.orderPrice = orderPrice.trim();
      if (customer.trim()) body.customer = customer.trim();
      if (worker) body.worker = worker;
      if (transferMethod === 'targetedSnipe') {
        body.autoFinishCycle = autoFinishCycle ? 1 : 0;
        if (autoFinishCycle && cyclePrice.trim()) body.cyclePrice = Number(cyclePrice);
        if (topUpEnabled && topUpThreshold.trim() && Number(topUpThreshold) > 0) {
          body.topUpEnabled = Number(topUpThreshold);
        }
      }

      const res = await fetch('/api/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminKey.trim()}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to create order');
      } else {
        setResult(data.trackingUrl);
        setFigures(
          typeof data.amountPurchased === 'number' && typeof data.amountNet === 'number'
            ? { gross: data.amountPurchased, net: data.amountNet, pct: Number(data.eaTaxPct) || EA_TAX_PCT }
            : null,
        );
        setCustomerName('');
        setAmount('');
        setBuyNowThreshold('');
        setOrderPrice('');
        setCustomer('');
        setCyclePrice('');
      }
    } catch {
      setError('Network error — check your connection');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    const name = cancelName.trim();
    if (!name || !adminKey.trim()) return;
    setCancelLoading(true);
    setCancelError(null);
    setCancelResult(null);
    try {
      const res = await fetch(`/api/orders/${encodeURIComponent(name)}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminKey.trim()}`,
        },
      });
      const data = await res.json();
      if (!res.ok) setCancelError(data.error || 'Failed to cancel order');
      else if (data.futStopped === false) {
        setCancelError(data.warning || 'Cancelled here, but FutTransfer is still running this order — abort it in the FutTransfer dashboard NOW.');
        setCancelName('');
      } else { setCancelResult('success'); setCancelName(''); }
    } catch {
      setCancelError('Network error — check your connection');
    } finally {
      setCancelLoading(false);
    }
  };

  const getWelcomeMessage = (link: string) =>
`Hello and welcome! 👋 Thank you for choosing our FC 27 coin service.
${figures ? `
Your order: ${figures.gross.toLocaleString()}K coins
You will receive: ${figures.net.toLocaleString()}K coins (EA takes ${figures.pct}% of every coin sale)
` : ''}
To get started, please click your order link below:
🔗 ${link}

🛡️ Transfer method: Comfort Trade — 100% safe & secure

Our process is fully automated — coins will be delivered via our custom FIFA 27 system with no manual intervention.

Once your details are submitted, your order will be processed promptly. 🚀`;

  const handleCopy = () => {
    if (!result) return;
    navigator.clipboard.writeText(getWelcomeMessage(result));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="tc-page">
      <nav className="tc-nav">
        <div className="tc-nav-brand">
          <img
            src="https://cdn.911gamingstore.com/images/1774435482275-425502583.png"
            alt="911GamingStore"
            className="tc-nav-logo"
          />
          <span className="tc-nav-name">911GamingStore</span>
        </div>
        <span className="tc-nav-badge">Employee Panel</span>
      </nav>

      {/* Coins and Robux are kept on separate tabs: different order fields,
          separate order lists, and separate profit totals. */}
      <div className="rbx-tabs">
        <button
          className={`rbx-tab${tab === 'coins' ? ' rbx-tab-active' : ''}`}
          onClick={() => switchTab('coins')}
        >
          <i className="fas fa-futbol"></i> FC Coins
        </button>
        <button
          className={`rbx-tab${tab === 'robux' ? ' rbx-tab-active' : ''}`}
          onClick={() => switchTab('robux')}
        >
          <i className="fas fa-cube"></i> Robux
        </button>
      </div>

      {tab === 'robux' && <RobuxPanel />}

      {/* Every coins card stays mounted only on its own tab. */}
      {tab === 'coins' && (<>
      <div className="tc-card">
        <div className="tc-card-header">
          <div className="tc-card-header-icon">
            <i className="fas fa-plus-circle"></i>
          </div>
          <div className="tc-card-header-text">
            <h2>New Coin Order</h2>
            <p>Create a delivery and generate a tracking link</p>
          </div>
        </div>

        <div className="tc-card-body">
          {error && (
            <div className="tc-alert tc-alert-danger">
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}

          {result ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="tc-alert tc-alert-success">
                <i className="fas fa-check-circle"></i>
                Order created! Copy the message below and send it to the customer:
              </div>
              <textarea
                readOnly
                value={getWelcomeMessage(result)}
                style={{
                  width: '100%',
                  minHeight: 200,
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  color: 'var(--text)',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                  padding: '12px 14px',
                  resize: 'none',
                  boxSizing: 'border-box',
                  cursor: 'text',
                }}
              />
              <button className="tc-btn tc-btn-primary" onClick={handleCopy}>
                <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`}></i>
                {copied ? 'Copied!' : 'Copy Message'}
              </button>
              <button
                className="tc-btn tc-btn-site"
                onClick={() => { setResult(null); setError(null); }}
                style={{ alignSelf: 'flex-start' }}
              >
                <i className="fas fa-plus"></i>
                New Order
              </button>
            </div>
          ) : (
            <div className="tc-form">
              <div className="tc-field">
                <label className="tc-label">Admin Key</label>
                <input
                  className="tc-input"
                  type="password"
                  placeholder="Your admin key"
                  value={adminKey}
                  onChange={e => setAdminKey(e.target.value)}
                  autoComplete="current-password"
                />
              </div>

              <div className="tc-field">
                <label className="tc-label">
                  Customer Name
                  <span className="tc-label-hint">— Eldorado Order ID</span>
                </label>
                <input
                  className="tc-input"
                  type="text"
                  placeholder="e.g. 12345678"
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleCreate()}
                />
              </div>

              <div className="tc-form-row">
                <div className="tc-field">
                  <label className="tc-label">
                    Amount
                    <span className="tc-label-hint">in K coins</span>
                  </label>
                  <input
                    className="tc-input"
                    type="number"
                    placeholder="e.g. 500"
                    min="1"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleCreate()}
                  />
                </div>
                <div className="tc-field">
                  <label className="tc-label">Platform</label>
                  <select
                    className="tc-input tc-select"
                    value={platform}
                    onChange={e => setPlatform(e.target.value)}
                  >
                    <option value="PS">PlayStation</option>
                    <option value="XB">Xbox</option>
                    <option value="PC">PC</option>
                  </select>
                </div>
              </div>

              <div className="tc-field">
                <label className="tc-label">
                  Coin Source
                  <span className="tc-label-hint">— our sellers are customers selling coins to the store</span>
                </label>
                <select
                  className="tc-input tc-select"
                  value={coinSource}
                  onChange={e => setCoinSource(e.target.value as 'auto' | 'own' | 'market')}
                >
                  <option value="auto">Auto — our sellers if they can cover it, else the market</option>
                  <option value="own">Our sellers only</option>
                  <option value="market">Market only (FutTransfer suppliers)</option>
                </select>
              </div>

              <div className="tc-field">
                <label className="tc-label">Transfer Method</label>
                <select
                  className="tc-input tc-select"
                  value={transferMethod}
                  onChange={e => { setTransferMethod(e.target.value); setBuyNowThreshold(''); setSupplierID(''); }}
                >
                  {METHODS.map(m => (
                    <option key={m.value} value={m.value}>{m.label} — {m.desc}</option>
                  ))}
                </select>
              </div>

              {coinSource !== 'own' && transferMethod === 'snipe' && (
                <div className="tc-field">
                  <label className="tc-label">
                    Supplier ID
                    <span className="tc-label-hint">— required for Legacy Snipe</span>
                  </label>
                  <input
                    className="tc-input"
                    type="text"
                    placeholder="MD5 or SHA1 of supplier email"
                    value={supplierID}
                    onChange={e => setSupplierID(e.target.value)}
                  />
                </div>
              )}

              {coinSource !== 'own' && needsThreshold(transferMethod) && (
                <div className="tc-form-row">
                  <div className="tc-field">
                    <label className="tc-label">
                      Max Price per 100K
                      <span className="tc-label-hint">— in USD (required)</span>
                    </label>
                    <input
                      className="tc-input"
                      type="number"
                      placeholder="e.g. 0.41"
                      min="0.01"
                      step="0.01"
                      value={buyNowThreshold}
                      onChange={e => setBuyNowThreshold(e.target.value)}
                    />
                  </div>
                  {transferMethod === 'targetedSnipe' && (
                    <div className="tc-field">
                      <label className="tc-label">Risk Level</label>
                      <select
                        className="tc-input tc-select"
                        value={riskLevel}
                        onChange={e => setRiskLevel(e.target.value)}
                      >
                        {RISK_LEVELS.map(r => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {coinSource === 'own' && transferMethod === 'targetedSnipe' && (
                <div className="tc-field">
                  <label className="tc-label">Risk Level</label>
                  <select
                    className="tc-input tc-select"
                    value={riskLevel}
                    onChange={e => setRiskLevel(e.target.value)}
                  >
                    {RISK_LEVELS.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {transferMethod === 'targetedSnipe' && (
                <div className="tc-field" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={autoFinishCycle}
                      onChange={e => setAutoFinishCycle(e.target.checked)}
                      style={{ width: 18, height: 18, accentColor: 'var(--primary)', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      Auto-finish via Cycle on daily limit
                    </span>
                  </label>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '6px 0 0 28px' }}>
                    When the Surgical Snipe hits the daily limit, automatically continue delivery using the Cycle method instead of stopping.
                  </p>
                  {autoFinishCycle && (
                    <div style={{ marginTop: 10, marginLeft: 28 }}>
                      <label className="tc-label" style={{ marginBottom: 4 }}>
                        Cycle Price per 100K
                        <span className="tc-label-hint">— in USD (optional — defaults to snipe price)</span>
                      </label>
                      <input
                        className="tc-input"
                        type="number"
                        placeholder="e.g. 0.45"
                        min="0.01"
                        step="0.01"
                        value={cyclePrice}
                        onChange={e => setCyclePrice(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              )}

              {transferMethod === 'targetedSnipe' && (
                <div className="tc-field" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={topUpEnabled}
                      onChange={e => setTopUpEnabled(e.target.checked)}
                      style={{ width: 18, height: 18, accentColor: 'var(--primary)', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      Enable Top-Up
                    </span>
                  </label>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '6px 0 0 28px' }}>
                    Minimum coins threshold (in K) required in account before top-up triggers. Only applies to Surgical Snipe.
                  </p>
                  {topUpEnabled && (
                    <div style={{ marginTop: 10, marginLeft: 28 }}>
                      <label className="tc-label" style={{ marginBottom: 4 }}>
                        Top-Up Threshold
                        <span className="tc-label-hint">— in K coins (0–9999, default 150)</span>
                      </label>
                      <input
                        className="tc-input"
                        type="number"
                        placeholder="e.g. 150"
                        min="1"
                        max="9999"
                        value={topUpThreshold}
                        onChange={e => setTopUpThreshold(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              )}

              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
                <div className="tc-form-row">
                  <div className="tc-field">
                    <label className="tc-label">
                      Order Price
                      <span className="tc-label-hint">— what customer paid ($)</span>
                    </label>
                    <input
                      className="tc-input"
                      type="number"
                      placeholder="e.g. 4.99"
                      min="0"
                      step="0.01"
                      value={orderPrice}
                      onChange={e => setOrderPrice(e.target.value)}
                    />
                  </div>
                  <div className="tc-field">
                    <label className="tc-label">Worker</label>
                    <select
                      className="tc-input tc-select"
                      value={worker}
                      onChange={e => setWorker(e.target.value)}
                    >
                      <option value="">— Select —</option>
                      {WORKERS.map(w => <option key={w} value={w}>{w}</option>)}
                    </select>
                  </div>
                </div>
                <div className="tc-field">
                  <label className="tc-label">
                    Customer
                    <span className="tc-label-hint">— Eldorado username</span>
                  </label>
                  <input
                    className="tc-input"
                    type="text"
                    placeholder="e.g. SlickPalm-5En5"
                    value={customer}
                    onChange={e => setCustomer(e.target.value)}
                  />
                </div>
              </div>

              <button
                className="tc-btn tc-btn-primary tc-btn-block"
                onClick={handleCreate}
                disabled={loading || !customerName.trim() || !amount || !adminKey.trim() ||
                  (needsThreshold(transferMethod) && !buyNowThreshold.trim()) ||
                  (transferMethod === 'snipe' && !supplierID.trim())}
              >
                <i className={`fas ${loading ? 'fa-circle-notch fa-spin' : 'fa-paper-plane'}`}></i>
                {loading ? 'Creating…' : 'Create Order & Get Link'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Cancel Order Card ─────────────────────────────── */}
      <div className="tc-card" style={{ marginTop: 16 }}>
        <div className="tc-card-header">
          <div className="tc-card-header-icon" style={{ color: 'var(--danger)' }}>
            <i className="fas fa-ban"></i>
          </div>
          <div className="tc-card-header-text">
            <h2>Cancel Order</h2>
            <p>Stop an active order and mark it as cancelled</p>
          </div>
        </div>
        <div className="tc-card-body">
          {cancelError && (
            <div className="tc-alert tc-alert-danger">
              <i className="fas fa-exclamation-circle"></i>
              {cancelError}
            </div>
          )}
          {cancelResult === 'success' && (
            <div className="tc-alert tc-alert-success">
              <i className="fas fa-check-circle"></i>
              Order cancelled successfully. The customer will see a cancellation notice.
            </div>
          )}
          <div className="tc-ba-form">
            <input
              className="tc-ba-input"
              type="text"
              placeholder="Customer name / Order ID to cancel"
              value={cancelName}
              onChange={e => { setCancelName(e.target.value); setCancelResult(null); setCancelError(null); }}
              disabled={cancelLoading}
              onKeyDown={e => e.key === 'Enter' && handleCancel()}
            />
            <button
              className="tc-btn tc-btn-primary"
              style={{ background: 'var(--danger)', flexShrink: 0 }}
              onClick={handleCancel}
              disabled={cancelLoading || !cancelName.trim() || !adminKey.trim()}
            >
              <i className={`fas ${cancelLoading ? 'fa-circle-notch fa-spin' : 'fa-ban'}`}></i>
              {cancelLoading ? 'Cancelling…' : 'Cancel Order'}
            </button>
          </div>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>
            If the order is already running on FutTransfer, it will be stopped automatically.
            The admin key entered above is used for authentication.
          </p>
        </div>
      </div>

      {/* ── Sync Completed Orders Card ────────────────────── */}
      <div className="tc-card" style={{ marginTop: 16 }}>
        <div className="tc-card-header">
          <div className="tc-card-header-icon" style={{ color: 'var(--success, #16a34a)' }}>
            <i className="fas fa-sync"></i>
          </div>
          <div className="tc-card-header-text">
            <h2>Sync Completed Orders</h2>
            <p>Check all active orders and mark finished ones as delivered in the sheet</p>
          </div>
        </div>
        <div className="tc-card-body">
          {sweepError && (
            <div className="tc-alert tc-alert-danger">
              <i className="fas fa-exclamation-circle"></i>
              {sweepError}
            </div>
          )}
          {sweepResult && (
            <div className="tc-alert tc-alert-success">
              <i className="fas fa-check-circle"></i>
              {sweepResult}
            </div>
          )}
          <button
            className="tc-btn tc-btn-primary tc-btn-block"
            onClick={handleSweep}
            disabled={sweepLoading || !adminKey.trim()}
          >
            <i className={`fas ${sweepLoading ? 'fa-circle-notch fa-spin' : 'fa-sync'}`}></i>
            {sweepLoading ? 'Checking all active orders…' : 'Check & Sync Now'}
          </button>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '8px 0 0' }}>
            Scans every order still delivering, asks FutTransfer if it finished, and updates the Google Sheet.
            Uses the admin key entered above.
          </p>
        </div>
      </div>

      {/* ── Validate Sheet Card ───────────────────────────── */}
      <div className="tc-card" style={{ marginTop: 16 }}>
        <div className="tc-card-header">
          <div className="tc-card-header-icon" style={{ color: '#f59e0b' }}>
            <i className="fas fa-clipboard-check"></i>
          </div>
          <div className="tc-card-header-text">
            <h2>Validate Ledger</h2>
            <p>Find orders missing from the ledger DB and backfill them</p>
          </div>
        </div>
        <div className="tc-card-body">
          {auditError && (
            <div className="tc-alert tc-alert-danger">
              <i className="fas fa-exclamation-circle"></i>
              {auditError}
            </div>
          )}
          {addResult && (
            <div className="tc-alert tc-alert-success">
              <i className="fas fa-check-circle"></i>
              {addResult}
            </div>
          )}

          <button
            className="tc-btn tc-btn-primary tc-btn-block"
            onClick={handleAudit}
            disabled={auditLoading || !adminKey.trim()}
          >
            <i className={`fas ${auditLoading ? 'fa-circle-notch fa-spin' : 'fa-clipboard-check'}`}></i>
            {auditLoading ? 'Checking…' : 'Check for Missing Orders'}
          </button>

          {missing && missing.length === 0 && (
            <div className="tc-alert tc-alert-success" style={{ marginTop: 12 }}>
              <i className="fas fa-check-circle"></i>
              All orders are in the ledger. Nothing missing. 🎉
            </div>
          )}

          {missing && missing.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <strong style={{ fontSize: '0.9rem' }}>{missing.length} order(s) missing from the ledger</strong>
                <button
                  className="tc-btn tc-btn-site"
                  style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                  onClick={() => {
                    const allOn = missing.every(m => selected[m.name]);
                    const next: Record<string, boolean> = {};
                    missing.forEach(m => { next[m.name] = !allOn; });
                    setSelected(next);
                  }}
                >
                  Toggle all
                </button>
              </div>

              <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                {missing.map(m => (
                  <label
                    key={m.name}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderBottom: '1px solid var(--border)', cursor: 'pointer', fontSize: '0.82rem' }}
                  >
                    <input
                      type="checkbox"
                      checked={!!selected[m.name]}
                      onChange={e => setSelected(s => ({ ...s, [m.name]: e.target.checked }))}
                      style={{ width: 16, height: 16, flexShrink: 0 }}
                    />
                    <span style={{ flex: 1, overflow: 'hidden' }}>
                      <span style={{ fontFamily: 'monospace' }}>{m.name}</span>
                      <span style={{ color: 'var(--text-muted)' }}>
                        {' '}· {m.amount}K{m.worker ? ` · ${m.worker}` : ''}{m.customer ? ` · ${m.customer}` : ''} · {m.status}
                      </span>
                    </span>
                  </label>
                ))}
              </div>

              <button
                className="tc-btn tc-btn-primary tc-btn-block"
                style={{ marginTop: 10 }}
                onClick={handleAddSelected}
                disabled={addLoading}
              >
                <i className={`fas ${addLoading ? 'fa-circle-notch fa-spin' : 'fa-plus'}`}></i>
                {addLoading ? 'Adding…' : `Add selected to ledger`}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Convert Cycle → Surgical Card ─────────────────── */}
      <div className="tc-card" style={{ marginTop: 16 }}>
        <div className="tc-card-header">
          <div className="tc-card-header-icon" style={{ color: '#8b5cf6' }}>
            <i className="fas fa-bolt"></i>
          </div>
          <div className="tc-card-header-text">
            <h2>Convert Cycle → Surgical</h2>
            <p>After you abort a cycle order in the dashboard, switch it back to the faster surgical method (with its original risk level) once the account cooldown is ready</p>
          </div>
        </div>
        <div className="tc-card-body">
          {surgError && (
            <div className="tc-alert tc-alert-danger">
              <i className="fas fa-exclamation-circle"></i>
              {surgError}
            </div>
          )}
          {surgResult && (
            <div className="tc-alert tc-alert-success">
              <i className="fas fa-check-circle"></i>
              {surgResult}
            </div>
          )}

          <button
            className="tc-btn tc-btn-primary tc-btn-block"
            onClick={handleSurgicalScan}
            disabled={surgLoading || !adminKey.trim()}
          >
            <i className={`fas ${surgLoading ? 'fa-circle-notch fa-spin' : 'fa-bolt'}`}></i>
            {surgLoading ? 'Scanning active cycle orders…' : 'Scan for Convertible Orders'}
          </button>

          {candidates && candidates.length === 0 && (
            <div className="tc-alert tc-alert-success" style={{ marginTop: 12 }}>
              <i className="fas fa-check-circle"></i>
              No cycle orders to convert right now. 🎉
            </div>
          )}

          {candidates && candidates.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <strong style={{ fontSize: '0.9rem' }}>{candidates.length} order(s) on cycle</strong>
                <button
                  className="tc-btn tc-btn-site"
                  style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                  onClick={() => {
                    const eligible = candidates.filter(c => c.readyToConvert && c.isReady && c.pending >= 6);
                    const allOn = eligible.length > 0 && eligible.every(c => surgSelected[c.name]);
                    const next = { ...surgSelected };
                    eligible.forEach(c => { next[c.name] = !allOn; });
                    setSurgSelected(next);
                  }}
                >
                  Toggle all ready
                </button>
              </div>

              <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                {candidates.map(c => {
                  const eligible = c.readyToConvert && c.isReady && c.pending >= 6;
                  // State badge: must be aborted (readyToConvert) AND past cooldown to convert.
                  const badge = !c.readyToConvert
                    ? { text: 'Running — abort first', color: '#f59e0b', dot: '○ ' }
                    : !c.isReady
                      ? { text: `Cooldown ${c.hours != null ? `${c.hours}h ` : ''}${c.minutes != null ? `${c.minutes}m` : `${Math.ceil(c.cooldownRemaining / 60)}m`}`.trim(), color: '#f59e0b', dot: '○ ' }
                      : { text: 'Ready to convert', color: 'var(--success, #16a34a)', dot: '● ' };
                  return (
                    <div
                      key={c.name}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                        borderBottom: '1px solid var(--border)', fontSize: '0.82rem',
                        opacity: eligible ? 1 : 0.55,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!surgSelected[c.name]}
                        disabled={!eligible}
                        onChange={e => setSurgSelected(s => ({ ...s, [c.name]: e.target.checked }))}
                        style={{ width: 16, height: 16, flexShrink: 0 }}
                      />
                      <span style={{ flex: 1, overflow: 'hidden' }}>
                        <span style={{ fontFamily: 'monospace' }}>{c.name}</span>
                        <span style={{ color: 'var(--text-muted)' }}>
                          {' '}· {c.pending}K left · {c.origin === 'cycle' ? 'cycle' : 'was surgical'}
                          {c.pending < 6 ? ' · below 6K min' : ''}
                        </span>
                        <span style={{ marginLeft: 8, fontWeight: 600, color: badge.color }}>
                          {badge.dot}{badge.text}
                        </span>
                      </span>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, color: 'var(--text-muted)' }}>
                        Risk
                        <select
                          value={surgRisk[c.name] ?? String(c.savedRiskLevel ?? 4)}
                          disabled={!eligible}
                          onChange={e => setSurgRisk(r => ({ ...r, [c.name]: e.target.value }))}
                          style={{ padding: '2px 4px', borderRadius: 6, border: '1px solid var(--border)' }}
                        >
                          {RISK_LEVELS.map(rl => (
                            <option key={rl.value} value={rl.value}>{rl.value}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                  );
                })}
              </div>

              <button
                className="tc-btn tc-btn-primary tc-btn-block"
                style={{ marginTop: 10 }}
                onClick={handleConvertSurgical}
                disabled={convertLoading || !candidates.some(c => surgSelected[c.name])}
              >
                <i className={`fas ${convertLoading ? 'fa-circle-notch fa-spin' : 'fa-bolt'}`}></i>
                {convertLoading ? 'Converting…' : 'Convert selected to Surgical'}
              </button>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '8px 0 0' }}>
                Switches the order to surgical and re-applies the chosen risk level. The order must be
                aborted first — use <strong>"Abort at next possibility"</strong> in the FutTransfer dashboard
                (there's no safe API for it), then Scan again. Only aborted orders past their cooldown with ≥ 6K
                remaining can be converted; running ones show <em>"abort first"</em>.
              </p>
            </div>
          )}
        </div>
      </div>
      </>)}

      <div className="tc-footer">
        © {new Date().getFullYear()} 911GamingStore · Employee Panel
      </div>
    </div>
  );
};

export default CreateOrderPage;
